import React, { useEffect, useRef, useState } from "react";
import { useAddNewReviewMutation, useUpdateReviewMutation } from "../features/review/reviewsApiSlice";
import useAuth from '../hooks/useAuth';

const AddReview = ({ productId, need, useFor, reviewId, content = "", offerCurrency = "ETB", offerValue = "", handleSaveButtonClick, handleReplyButtonClick, refetch, children }) => {
  const { id } = useAuth();
  const [addNewReview, { isSuccess, isError, error }] = useAddNewReviewMutation();
  const [updateReview, { isSuccess: updateSuccess, isError: updateIsError, error: updateError }] = useUpdateReviewMutation();

  const textareaRef = useRef(null);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const [isPriceInputFocused, setIsPriceInputFocused] = useState(false);
  const [isButtonDivVisible, setIsButtonDivVisible] = useState(useFor === "comment" ? false : true);
  const [textareaValue, setTextareaValue] = useState(content);
  const [priceValue, setPriceValue] = useState(offerValue);
  const [currency, setCurrency] = useState(offerCurrency);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Existing handlers remain the same
  const handleTextareaFocus = () => {
    setIsTextareaFocused(true);
    setIsButtonDivVisible(true);
  };

  const handlePriceInputFocus = () => {
    setIsPriceInputFocused(true);
    setIsButtonDivVisible(true);
  };

  const handlePriceInputBlur = () => {
    setIsPriceInputFocused(false);
  };

  const handleTextareaBlur = () => {
    setIsTextareaFocused(false);
  };

  const handleTextareaInput = () => {
    const textarea = textareaRef.current;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handleCancelClick = () => {
    setIsButtonDivVisible(false);
    setTextareaValue("");
    setCurrency("ETB");
    setPriceValue("");
    handleTextareaInput();
  };

  const handleTextareaChange = (event) => {
    setTextareaValue(event.target.value);
    handleTextareaInput();
  };

  const formatNumber = (number) => {
    const parts = number.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const onPriceValueChanged = (e) => {
    let rawValue = e.target.value.replace(/,/g, '');
    if (/^[0-9.]*$/.test(rawValue)) {
      const formattedValue = formatNumber(rawValue);
      setPriceValue(formattedValue);
    }
  };

  const handleCurrencyChange = (value) => {
    setCurrency(value);
    setIsOpen(false);
  };

  useEffect(() => {
    handleTextareaInput();
  }, [textareaValue]);

  useEffect(() => {
    if (isSuccess) {
      setIsButtonDivVisible(false);
      setTextareaValue("");
      setCurrency("ETB");
      setPriceValue("");
      handleTextareaInput();
      if(useFor === "reply") handleReplyButtonClick();
      refetch();
    } else if(isError) {
      console.log(error?.data?.message);
    } else if(updateSuccess) {
      handleSaveButtonClick();
      refetch();
    }
  }, [isSuccess, updateSuccess, handleSaveButtonClick, handleReplyButtonClick, refetch, useFor, isError, error]);

  const onAddReviewClicked = async () => {
    if(need === "Sell") {
      if(useFor === "reply") {
        await addNewReview({ user: id, replyTo: reviewId, product: productId, content: textareaValue });
      } else if(useFor === "comment") {
        await addNewReview({ user: id, product: productId, content: textareaValue });
      } else if(useFor === "edit") {
        await updateReview({ id: reviewId, content: textareaValue});
      }
    } else if(need === "Request") {
      if(useFor === "edit") {
        await updateReview({ 
          id: reviewId,
          content: textareaValue, 
          offer: { 
            currency: currency, 
            value: priceValue 
          }
        });
      } else {
        await addNewReview({ 
          user: id, 
          product: productId, 
          content: textareaValue, 
          offer: { 
            currency: currency, 
            value: priceValue 
          }
        });
      }
    }
  }

  return (
    <div
      className={`w-full rounded-lg bg-white shadow-lg p-4 ${useFor === "reply" ? "ml-14" : "mt-4"}
        transition-all duration-300 ease-out transform 
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
    >
      {need === "Sell" ? (
        <div className="space-y-4">
          <div className={`relative ${useFor === "comment" ? "w-full" : "w-full"}`}>
            <textarea
              ref={textareaRef}
              className={`w-full p-3 rounded-lg text-sm transition-all duration-200 resize-none outline-none border-2 
                ${isTextareaFocused ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"}`}
              rows="1"
              placeholder={`Add a ${useFor}...`}
              onFocus={handleTextareaFocus}
              onBlur={handleTextareaBlur}
              onInput={handleTextareaInput}
              value={textareaValue}
              onChange={handleTextareaChange}
            />
          </div>

          {isButtonDivVisible && (
            <div className="flex justify-end items-center gap-3 transition-all duration-300 ease-out">
              {useFor === "comment" && (
                <button 
                  onClick={handleCancelClick}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
              )}
              {children}
              <button
                disabled={textareaValue === ""}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                  ${textareaValue === "" 
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed" 
                    : "bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700"
                  }`}
                onClick={onAddReviewClicked}
              >
                {useFor === "edit" ? "Save" : useFor === "reply" ? "Reply" : "Comment"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-6">
            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="currency"
                  value="ETB"
                  checked={currency === "ETB"}
                  onChange={() => handleCurrencyChange("ETB")}
                  className="w-4 h-4 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">ETB</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="currency"
                  value="$"
                  checked={currency === "$"}
                  onChange={() => handleCurrencyChange("$")}
                  className="w-4 h-4 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">USD</span>
              </label>
            </div>
          </div>

          <div className="space-y-4 transition-all duration-300 ease-out">
            <input
              type="text"
              onChange={onPriceValueChanged}
              onFocus={handlePriceInputFocus}
              onBlur={handlePriceInputBlur}
              value={priceValue}
              className={`w-full p-3 rounded-lg text-sm transition-all duration-200 border-2
                ${isPriceInputFocused ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"}`}
              placeholder="0.00"
            />

            <textarea
              ref={textareaRef}
              className={`w-full p-3 rounded-lg text-sm transition-all duration-200 resize-none border-2
                ${isTextareaFocused ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"}`}
              rows="1"
              placeholder="Product description"
              onFocus={handleTextareaFocus}
              onBlur={handleTextareaBlur}
              onInput={handleTextareaInput}
              value={textareaValue}
              onChange={handleTextareaChange}
            />

            {isButtonDivVisible && (
              <div className="flex justify-end items-center gap-3 transition-all duration-300 ease-out">
                {useFor === "comment" && (
                  <button 
                    onClick={handleCancelClick}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                )}
                {children}
                <button
                  disabled={textareaValue === "" || priceValue === ""}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                    ${textareaValue === "" || priceValue === "" 
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed" 
                      : "bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700"
                    }`}
                  onClick={onAddReviewClicked}
                >
                  {useFor === "edit" ? "Save" : "Offer"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddReview;